import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import RegistrationSuccess from '@/components/auth/RegistrationSuccess';
import PaymentVerification from '@/components/auth/PaymentVerification';
import PaymentForm from '@/components/auth/PaymentForm';
import RegistrationForm from '@/components/auth/RegistrationForm';
import SupportBanner from '@/components/auth/SupportBanner';
import { useRegistrationForm } from '@/hooks/useRegistrationForm';
import { useMailerLiteSync } from '@/hooks/useMailerLiteSync';
import { usePaymentVerification } from '@/hooks/usePaymentVerification';
import { useStripeCheckout } from '@/hooks/useStripeCheckout'; 
import { useReferralTracking } from '@/hooks/useReferralTracking';
import { useStateRecovery } from '@/hooks/useStateRecovery';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { user } = auth;

  // Initialize referral tracking
  useReferralTracking();
  
  // Recuperar estado si el usuario vuelve de una redirección bancaria
  const { recoveredState, clearSavedState, hasRecoveredState } = useStateRecovery();
  
  // Setup form state management
  const {
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    acceptTerms,
    setAcceptTerms,
    acceptPrivacy,
    setAcceptPrivacy,
    isSubmitting,
    hasPaid,
    setHasPaid,
    registrationSuccess,
    paymentError,
    setPaymentError,
    showWelcomePopup,
    setShowWelcomePopup,
    validationErrors,
    handleSubmit
  } = useRegistrationForm({
    navigate,
    handleSubscribe: () => {},  // We'll replace this below
    verifiedSessionId: '',      // We'll replace this below
    auth
  });
  
  // Setup MailerLite integration
  const { addEmailToMailerLite } = useMailerLiteSync();

  // Setup payment verification
  const {
    isVerifying,
    verifiedSessionId,
    verifyPaymentSession
  } = usePaymentVerification({
    setEmail,
    setHasPaid,
    setPaymentError
  });
  
  // Setup Stripe checkout
  const { handleSubscribe, isRedirecting } = useStripeCheckout({
    email,
    name: "", // We'll pass an empty string for name since we removed the field
    addEmailToMailerLite,
    setPaymentError
  });

  // RECUPERAR ESTADO de forma más aggressiva cuando el componente se monta
  useEffect(() => {
    console.log("[REGISTER] Verificando estado recuperado:", recoveredState);
    console.log("[REGISTER] Estado actual - email:", email, "name:", name);
    
    if (recoveredState && !registrationSuccess && !hasPaid) {
      console.log("[REGISTER] Aplicando estado recuperado:", recoveredState);
      
      // Mostrar toast informativo inmediatamente
      toast.info("Restaurando tu proceso de registro...", {
        description: "Hemos detectado que volviste de un pago, restaurando tus datos",
        duration: 4000
      });
      
      // Restaurar email si está disponible y el campo está vacío
      if (recoveredState.email && !email) {
        console.log("[REGISTER] Restaurando email:", recoveredState.email);
        setEmail(recoveredState.email);
      }
      
      // Restaurar nombre si está disponible y el campo está vacío
      if (recoveredState.name && !name) {
        console.log("[REGISTER] Restaurando nombre:", recoveredState.name);
        setName(recoveredState.name);
      }

      // Restaurar referral ID si está disponible
      if (recoveredState.referralId) {
        console.log("[REGISTER] Restaurando referral ID:", recoveredState.referralId);
        localStorage.setItem('rewardful_referral_id', recoveredState.referralId);
        sessionStorage.setItem('rewardful_referral_id', recoveredState.referralId);
      }
    }
  }, [recoveredState, setEmail, setName, email, name, registrationSuccess, hasPaid]);
  
  // Verificar también si hay flags de checkout en progreso
  useEffect(() => {
    const checkoutInProgress = localStorage.getItem('checkout_in_progress') || sessionStorage.getItem('checkout_in_progress');
    if (checkoutInProgress === 'true') {
      console.log("[REGISTER] Detectado checkout en progreso, verificando estado...");
      
      // Si hay un checkout en progreso pero no tenemos estado recuperado, 
      // intentar recuperar datos manualmente
      if (!recoveredState) {
        const savedEmail = localStorage.getItem('pending_email') || sessionStorage.getItem('pending_email');
        const savedName = localStorage.getItem('pending_name') || sessionStorage.getItem('pending_name');
        const referralId = localStorage.getItem('rewardful_referral_id') || sessionStorage.getItem('rewardful_referral_id');
        
        if (savedEmail || savedName || referralId) {
          console.log("[REGISTER] Recuperando datos de checkout en progreso:", { savedEmail, savedName, referralId });
          
          if (savedEmail && !email) setEmail(savedEmail);
          if (savedName && !name) setName(savedName);
          
          toast.success("Datos de pago restaurados", {
            description: "Hemos recuperado tu información del proceso de pago",
            duration: 3000
          });
        }
      }
    }
  }, [recoveredState, email, name, setEmail, setName]);
  
  // Check URL parameters for payment success/session ID on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const paymentSuccess = queryParams.get('payment_success');
    const paymentSessionId = queryParams.get('session_id');
    const paymentEmail = queryParams.get('email');
    
    console.log("URL params check:", { paymentSuccess, paymentSessionId, paymentEmail });
    
    if (paymentSuccess === 'true' && paymentSessionId) {
      console.log("Payment successful, verifying session:", paymentSessionId);
      
      // Limpiar estado guardado ya que el pago fue exitoso
      clearSavedState();
      
      if (paymentEmail) {
        setEmail(paymentEmail);
      }
      verifyPaymentSession(paymentSessionId, paymentEmail || '');
    }
  }, [location, setEmail, verifyPaymentSession, clearSavedState]);
  
  // Check if already logged in
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Función para limpiar estado manualmente
  const handleClearState = () => {
    clearSavedState();
    
    // También limpiar flags adicionales
    localStorage.removeItem('checkout_in_progress');
    sessionStorage.removeItem('checkout_in_progress');
    localStorage.removeItem('referral_discount');
    
    setEmail('');
    setName('');
    setPaymentError('');
    toast.success("Estado limpiado", {
      description: "Puedes empezar de nuevo",
      duration: 2000
    });
  };

  // If registration is successful, show success screen
  if (registrationSuccess) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow flex items-center justify-center py-12 px-4">
          <RegistrationSuccess 
            email={email} 
            name={name}
            showWelcomePopup={showWelcomePopup}
            setShowWelcomePopup={setShowWelcomePopup}
          />
        </main>
        <Footer />
      </div>
    );
  }

  // If verification is in progress, show loading screen
  if (isVerifying) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <main className="flex-grow flex items-center justify-center py-12 px-4">
          <PaymentVerification email={email} />
        </main>
        <Footer />
      </div>
    );
  }

  // Determine if we should show payment form or registration form
  const queryParams = new URLSearchParams(location.search);
  const paymentSuccess = queryParams.get('payment_success');
  const showRegistrationForm = hasPaid || paymentSuccess === 'true';

  // Main registration form
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      {/* Warning Banner - only show when user is filling registration form after payment */}
      {showRegistrationForm && (
        <div className="bg-blue-600 text-white py-3 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-2">
            <Info className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium text-center">
              ⚠️ Importante: No salgas de esta página hasta completar tu registro para no perder tu suscripción
            </p>
          </div>
        </div>
      )}
      
      
      
      <main className="flex-grow flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-vegana-primary">
                {showRegistrationForm ? 'Crear Cuenta' : 'Suscripción Requerida'}
              </CardTitle>
              <CardDescription>
                {showRegistrationForm 
                  ? paymentSuccess === 'true' 
                    ? '¡Pago exitoso! Ahora crea tu cuenta para acceder'
                    : 'Únete a Guía Vegana para acceder a todas las funcionalidades'
                  : 'Para registrarte en Guía Vegana, primero debes adquirir una suscripción'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!showRegistrationForm ? (
                <PaymentForm 
                  email={email}
                  setEmail={setEmail}
                  paymentError={paymentError}
                  handleSubscribe={handleSubscribe}
                  isRedirecting={isRedirecting}
                />
              ) : (
                <RegistrationForm
                  name={name}
                  setName={setName}
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  confirmPassword={confirmPassword}
                  setConfirmPassword={setConfirmPassword}
                  acceptTerms={acceptTerms}
                  setAcceptTerms={setAcceptTerms}
                  acceptPrivacy={acceptPrivacy}
                  setAcceptPrivacy={setAcceptPrivacy}
                  validationErrors={validationErrors}
                  handleSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              )}
            </CardContent>
            <CardFooter className="flex justify-center">
              <p className="text-sm text-gray-600">
                ¿Ya tienes una cuenta?{' '}
                <Link to="/login" className="text-vegana-primary hover:underline">
                  Inicia sesión
                </Link>
              </p>
            </CardFooter>
          </Card>
          
          {/* Support Banner */}
          <SupportBanner />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Register;
